import { useMsal } from "@azure/msal-react";
import { jwtDecode } from "jwt-decode";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { ProductFruits } from "react-product-fruits";

import { InitLoader } from "@/components/loaders/InitLoader";
import { TrialExpiredModal } from "@/components/modals/trial-expired-modal";
import {
  useCreateOrguserMutation,
  useLazyGetTenantIdQuery,
  useLazyGetUserIdQuery,
  useTrackEventMutation,
} from "@/features/user-manager";
import { ErrorComponent } from "@/features/user-manager/components/UserNotFound";
import { IUserMetadata } from "@/features/user-manager/types";
import { useAppDispatch } from "@/reduxHooks";
import { setAccountUserId, setCurrentUserMetadata } from "@/slices/auth-slice";
import { analyticsService } from "@/utils/analytics-service"; 
import { getRegionFromUrl } from "@/utils/baseUrls";
import { APP_BASE_URL, ENVIRONMENTS } from "@/utils/constants";
import { getBooleanEnv } from "@/utils/helper.ts";
import { CacheKey, cacheUtil } from "@/utils/localstorage";
const errorMessage = {
  "404":
    "You are not a member of this Organization.\nReach out to the Organization admins for more information.",
};

// Define a specific error code for trial expiration
const TRIAL_EXPIRED_ERROR_CODE = "TRIAL_EXPIRED";

 

const AppInitializer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [getTenant] = useLazyGetTenantIdQuery();
  const [getUserId] = useLazyGetUserIdQuery();
  const [createOrgUser] = useCreateOrguserMutation();
  const [trackUserEvent] = useTrackEventMutation();
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const activeAccount = instance.getActiveAccount();
  const [userInfo, setUserInfo] = useState<any>();

  useEffect(() => {
    const checkTenant = async () => {
      try {
        const storedToken = cacheUtil.getUnParsed(CacheKey.TenantId);
        if (!isEmpty(storedToken)) return storedToken;
        else {
          const response = await getTenant().unwrap();
          const newTenantId = response?.response?.data?.tenantId;
          if (newTenantId) {
            cacheUtil.set(CacheKey.TenantId, newTenantId);
            return newTenantId;
          }
        }
        return false;
      } catch (err) {
        console.log(err);
      }
    };

    const checkUserId = async () => {
      try {
        const jwtToken = activeAccount?.idToken as string;
        const oid = jwtDecode<{ oid: string }>(jwtToken).oid;

        const response = await getUserId({ oid }).unwrap();
        void trackUserEvent({});
        const newUserId = response?.response?.data?.userId as string;
        const userMetadata = response?.response?.data as IUserMetadata;
        if (newUserId) {
          cacheUtil.set(CacheKey.UserId, newUserId);
          dispatch(setCurrentUserMetadata(userMetadata));
          dispatch(setAccountUserId(newUserId));

          analyticsService.identifyUser({userProperties: {role: userMetadata.role, isTrialUser: userMetadata.isTrialUser.toString()}});

          return {
            userId: newUserId,
            userRole: userMetadata.role,
            isTrialUser: userMetadata.isTrialUser,
          };
        }
        // }
        return { userId: undefined, userRole: undefined };
      } catch (err: any) {
        if (
          err.data?.status === "error" &&
          err.data?.response?.type === "validation_error" &&
          err.data?.response?.errors?.[0]?.code === "invalid" &&
          err.data?.response?.errors?.[0]?.detail?.includes(
            "User trial has expired"
          )
        ) {
          setError(new Error(TRIAL_EXPIRED_ERROR_CODE));
        }  
        else {
          console.error("Failed to fetch credentials:", err);
          setError(
            new Error("Failed to fetch Credentials. Please reload the app")
          );
        }
      }
    };

    const tempUserSignUp = async (tenantId: string) => {
      try {
        const jwtToken = activeAccount?.idToken as string;
        const jwt: any = jwtDecode(jwtToken);
        const oid = jwt.oid;
        const first_name = jwt.given_name || "";
        const last_name = jwt.family_name || "";
        const email = jwt.emails[0] || "";
        const body = {
          "X-TENANT-ID": tenantId,
          first_name: first_name,
          last_name: last_name,
          email: email,
          oid: oid,
        };
        analyticsService.identifyUser({userProperties: {name: (first_name + last_name) || jwt.name, email: email, oid: oid}});
        await createOrgUser({ data: body }).unwrap();
        return true;
      } catch (e) {
        console.error("Error - User was created without signing up.", e);
        return false;
      }
    };

    const initializeApp = async () => {
      try {
        const tenantId = await checkTenant();
        if (!tenantId) {
          setError(new Error("Failed to initialize app"));
          return;
        }
        analyticsService.identifyUser({userProperties: {tenantId: tenantId}});

        await tempUserSignUp(tenantId);

        const user = await checkUserId();
        if (!isEmpty(user) || !isEmpty(tenantId)) {
          const isTrialUser = user?.isTrialUser;
          setUserInfo({
            username: user?.userId,
            role: isTrialUser ? "trial_user" : user?.userRole,
          });
          setIsInitialized(true);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch Credentials. Please reload the app")
        );
      }
    };

    initializeApp();
  }, []);

  const enableProductFruits = getBooleanEnv("VITE_ENABLE_PRODUCT_FRUITS");

  function redirectToSelectOrg() {
    const envPart = window.location.hostname
      .split(".")
      .find((part) => part === ENVIRONMENTS.DEV || part === ENVIRONMENTS.QA);

    const region = getRegionFromUrl();

    window.location.href = envPart
      ? `https://${region}.${envPart}.${APP_BASE_URL}`
      : `https://${region}.${APP_BASE_URL}`;
  }

  const isOnSelectOrgPage = window.location.pathname.endsWith("/select-org");

  // 1. Handle Trial Expiration
  if (error?.message === TRIAL_EXPIRED_ERROR_CODE) {
    return <TrialExpiredModal isOpen={true} />;
  }

  // 2. Handle Other Errors (including 404)
  if (error) {
    if (error?.message) {
      let errorDetails;
      try {
        errorDetails = JSON.parse(error.message);
      } catch (e) {
        errorDetails = error;
      }
      // If user is not found, i.e. not a member of any organization, redirect to select-org page
      if (errorDetails.status === 404) {
        if (isOnSelectOrgPage) {
          return children;
        } else {
          redirectToSelectOrg();
          return null;
        }
      }
      // If there is any other error, show the error component
      return <ErrorComponent error={errorDetails.message} />;
    } else {
      return <ErrorComponent error={error.message} />;
    }
  }

  // Or a more sophisticated loading component
  if (!isInitialized) {
    return <InitLoader />;
  }

  return (
    <div>
      {enableProductFruits && (
        <ProductFruits
          workspaceCode={import.meta.env.VITE_PRODUCT_FRUITS}
          language="en"
          user={userInfo}
        />
      )}
      {children}
    </div>
  );
};

export default AppInitializer;
